import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { LegalDisclaimer } from "@/components/shared/LegalDisclaimer";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { useSEO } from "@/lib/useSEO";
import { breadcrumbJsonLd, faqPageJsonLd, organizationJsonLd, informationalPageJsonLd } from "@/lib/seoSchema";

export const FAQS = [
  {
    question: "How do Statlign predictions work?",
    answer:
      "Statlign runs a library of independent statistical strategies over enriched match data — team form, tactics, player availability, historical patterns and live odds — and combines their signals into a single prediction with a confidence score and a plain-language explanation.",
  },
  {
    question: "How accurate are your predictions?",
    answer:
      "No prediction service can be right every time — sports are inherently uncertain. We publish our historical results transparently on the Track Record and Performance pages so you can judge accuracy yourself over a meaningful sample rather than a single week.",
  },
  {
    question: "Which sports and leagues do you cover?",
    answer:
      "Football (soccer) is our current flagship sport, covering major leagues, cups and international tournaments, with coverage expanding across more sports as Statlign grows into a full sports intelligence platform.",
  },
  {
    question: "How often are predictions updated?",
    answer:
      "The prediction feed refreshes continuously as new fixtures, odds and team data arrive, so upcoming matches always reflect the latest available information.",
  },
  {
    question: "What does 'value' or 'high-value odds' mean?",
    answer:
      "Value means the true probability of an outcome is higher than the probability implied by the bookmaker's odds. High-value odds are selections where our engine identifies a meaningful edge at longer prices — higher potential reward, and higher risk.",
  },
  {
    question: "How is your AI trained?",
    answer:
      "Our strategies are grounded in historical match data and statistically validated patterns, and the engine learns from previous outcomes — including rejected patterns — to avoid repeating weak selections. See the How Our AI Works page for detail.",
  },
  {
    question: "Can I place bets directly on Statlign?",
    answer:
      "No. Statlign is an informational and analytics platform, not a bookmaker. We do not accept wagers or facilitate gambling. Any betting you choose to do happens with a licensed operator, at your own risk.",
  },
  {
    question: "What are confidence scores?",
    answer:
      "Each prediction carries a confidence score reflecting how strongly our strategies agree and how complete the underlying data is. Higher-odds, higher-risk picks are treated conservatively so confidence is never artificially inflated.",
  },
  {
    question: "Do you have an API?",
    answer:
      "Yes — Statlign exposes an MCP server so AI agents and developers can access public predictions programmatically. See the MCP documentation for details.",
  },
  {
    question: "How do credits work?",
    answer:
      "Some features use credits: revealing a prediction and unlocking its strategy insights each cost a small number of credits. You receive a daily allowance, and can earn or purchase more.",
  },
  {
    question: "Are predictions guaranteed?",
    answer:
      "No. Predictions are probabilistic estimates for informational and entertainment purposes only. Past performance never guarantees future results. Always gamble responsibly.",
  },
  {
    question: "How can I contact support?",
    answer:
      "Use the contact form on our Contact page, or email statlign@gmail.com. We aim to respond within 1–2 business days.",
  },
];

export default function Faq() {
  const { t } = useTranslation();
  const crumbs = [
    { name: t("breadcrumbs.home"), path: "/" },
    { name: t("breadcrumbs.faq"), path: "/faq" },
  ];
  useSEO({
    title: t("seo.faq.title"),
    description: t("seo.faq.description"),
    canonicalPath: "/faq",
    jsonLd: [
      organizationJsonLd(),
      informationalPageJsonLd({
        type: "FAQPage",
        name: t("seo.faq.h1"),
        description: t("seo.faq.description"),
        path: "/faq",
      }),
    ],
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Breadcrumbs items={crumbs} />

      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{uiText("Frequently Asked Questions")}</h1>
        <p className="text-muted-foreground">
           {uiText("Everything you need to know about how Statlign works. Still stuck?")}{" "}
          <Link to="/contact" className="text-primary hover:underline">
             {uiText("Contact us")} </Link>
          .
        </p>
      </header>

      <h2 className="sr-only">{uiText("Common questions")}</h2>
      <Card>
        <CardContent className="py-2">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.question} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-foreground">{uiText(f.question)}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{uiText(f.answer)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      <LegalDisclaimer />
    </div>
  );
}
